import GetTaxes from '../../routes/get-taxes';
import GetPartners from '../../routes/get-partners';
import { ITaxList } from '../../models/taxes.interface';
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';
import { tableSearchInput } from './table-helpers';
import useAuth from '../../hooks/auth-hooks';
import AddNewTax from '../../components/forms/tax/add-new-tax';
import UpdateIndividualTax from '../../components/forms/tax/update-individual-tax';
import TaxModalDisplay from '../forms/tax/tax-modal';
import { TAX_FORM, TAX_TYPE } from '../forms/tax/form-tax-types';
import { dropdown } from '../dropdown';

/**
 * Function that gets a list of taxes.
 * 
 * @returns void
 */
 export const taxListTable = async () => {
  const taxTable = document.querySelector('.aafp_tax-list') as HTMLElement;
  if(!taxTable) return;

  const [ partner, tax ] = await Promise.all([ GetPartners(), GetTaxes() ]);

  displayTaxTable(tax.body, taxTable);

  const { accessType, accessLevel } = useAuth()!;
  let hasAccess = true;
  if ( accessType === 'E' && accessLevel === 'P' ) {  
    hasAccess = false;
  }

  if(partner) {
    const mapList = partner.body.map((r: any) => ({
      id: r.partnerID,
      name: r.partnerName
    }));
    dropdown(mapList, 'partner', 'Partner', true, true, hasAccess);
    dropdown(TAX_TYPE, 'tax-type', 'Tax Type', false, true, true);
  }

  const handleAddTaxModalClick = () => {
    const taxModal = document.querySelector('.aafp_modal') as HTMLElement;
    TaxModalDisplay(taxModal, 'Add New Tax');

    AddNewTax();
  };

  const addTaxModal = document.querySelector('#aafp_tax-add') as HTMLElement;
  if ( addTaxModal ) {
    addTaxModal.addEventListener('click', handleAddTaxModalClick);
  }
}

/**
 * Function handles parsing and displaying taxes in a table.
 * 
 * @param {ITaxList[]} data the api response data
 * @param {HTMLElement} taxTable the html container element for the tax table
 * 
 * @returns void
 */
const displayTaxTable = (data: ITaxList[], taxTable: HTMLElement) => {
  taxTable.innerHTML = '';

  const { accessType } = useAuth()!;

  const isRowSelected = (cell: any) => cell.getRow().isSelected();

  const edit_cellclick = (e: any, cell: CellComponent) => {
    const currentRow = cell.getData() as ITaxList;
    TaxModalDisplay(TAX_FORM.txModal, 'Update Tax', currentRow);
    UpdateIndividualTax();
  }

  const edit_button = (cell: CellComponent, formatterParams: any) => {
    const { taxId, taxName } = cell.getData() as ITaxList;
    return `<a role="button" 
                id="aag_edit-personnel_${taxId}" 
                class="aafp_btn-table btn_theme-primary" 
                data-toggle="tooltip"
                data-placement="bottom" 
                title="Edit ${taxName}">
              <i class="fas fa-pen"></i>
            </a>`;
  }

  const formatTax = (cell: CellComponent, formatterParams: any) => {
    const { taxType, taxRateCentsOrPercent } = cell.getData() as ITaxList;
    return taxType === 'C' ? `$${taxRateCentsOrPercent}` : `${taxRateCentsOrPercent}%`;
  }

  const hasAccess = accessType === 'E';

  const options: Options = {
    index:"ID",
    data,
    placeholder: "No Data Available",
    placeholderHeaderFilter: "No Matching Data",
    pagination:true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 100],
    paginationCounter: "rows",
    layoutColumnsOnNewData:true,
    selectableRows: false,
    columns: [
      { title: "ID", field: "taxId", visible: false },
      { title: "Name", field: "taxName", sorter: "string", resizable: false, vertAlign: 'middle', editable: isRowSelected, editor: "input", editorParams: { elementAttributes: { maxLength: 50 } } },
      { title: "Amount", field: "taxRateCentsOrPercent", formatter: formatTax, sorter: "number", editable: isRowSelected, vertAlign: 'middle', editor: "number", resizable: false },
      { title: "Type", field: "taxType", sorter: "string", editable: isRowSelected, vertAlign: 'middle', editor: 'list', editorParams: { values: { "P":"Percent", "C":"Cent per Gallon" } }, formatter: 'lookup', formatterParams: { "P":"Percent", "C":"Cent per Gallon" } },
      { title: "Partner", field: "partnerName", sorter: 'string', resizable: false, vertAlign: 'middle' },
      { title: '', field: "EditButton", formatter: edit_button, visible: hasAccess, cssClass: "aafp_column-button", hozAlign: 'center', width: 48, cellClick: edit_cellclick, headerSort: false, resizable: false }
    ]
  };

  const taxTableGrid = new TabulatorFull(taxTable, options); 
  const taxListSort = document.querySelector('#aafp_tax-list-sort') as HTMLInputElement;
  tableSearchInput(taxListSort, taxTableGrid);
}
